import { useState, forwardRef } from 'react';

import { useDateTimeContext } from '@rtkwlf/fenrir-react';
import { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Box, type BoxProps } from '@packages/fenrir/components/box';
import {
  type DateRange,
  type DateRangeValidationError,
  type PickerChangeHandlerContext,
} from '@packages/fenrir/components/date-pickers';
import {
  DateRangeInputField,
  DateRangePicker as FenrirDateRangePicker,
  type DateRangePickerProps as FenrirDateRangePickerProps,
} from '@packages/fenrir/components/date-pickers/date-range-picker';
import { FormLabel } from '@packages/fenrir/components/form-label';
import type { TextFieldProps } from '@packages/fenrir/components/text-field';
import {
  DatePickerShortcuts,
  useDatePickerShortcuts,
} from '@packages/hooks/use-date-picker-shortcuts/use-date-picker-shortcuts';

import { useDateRangePickerErrorMessages } from './use-date-range-picker-error-messages/use-date-range-picker-error-messages';

const defaultShortcuts = [
  DatePickerShortcuts.PastHour,
  DatePickerShortcuts.Past24,
  DatePickerShortcuts.Past72,
  DatePickerShortcuts.PastWeek,
];

export interface DateRangePickerProps {
  disabled?: boolean;
  errorMessage?: string;
  helperText?: { start?: string; end?: string };
  hideMultiLabels?: boolean;
  label?: string;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  name: string;
  onChange: (
    newValue: DateRange<Dayjs>,
    context: PickerChangeHandlerContext<DateRangeValidationError>,
  ) => void;
  onError?: (error: DateRangeValidationError, value: DateRange<Dayjs>) => void;
  pickerProps?: Omit<
    FenrirDateRangePickerProps,
    | 'disabled'
    | 'maxDate'
    | 'minDate'
    | 'name'
    | 'onError'
    | 'onChange'
    | 'slotProps'
    | 'timezone'
    | 'value'
  >;
  placeholder?: { start?: string; end?: string };
  required?: boolean;
  shortcuts?: DatePickerShortcuts[];
  textFieldProps?: Omit<
    TextFieldProps,
    'helperText' | 'id' | 'error' | 'placeholder' | 'required'
  >;
  value: DateRange<Dayjs>;
  wrapperProps?: BoxProps;
}

export const DateRangePicker = forwardRef(
  (
    {
      disabled,
      errorMessage,
      helperText,
      hideMultiLabels = true,
      label,
      minDate,
      maxDate,
      name,
      onChange,
      onError,
      pickerProps,
      placeholder,
      required,
      textFieldProps,
      shortcuts,
      value,
      wrapperProps,
    }: DateRangePickerProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const { timezone } = useDateTimeContext();
    const [dateRangePickerError, setDateRangePickerError] =
      useState<DateRangeValidationError | null>(null);

    const labelWithTimezone = label
      ? t('ui:timezone.label', {
          label,
          timezone: t(`ui:timezone.${timezone}`),
        })
      : undefined;

    const filteredShortcuts = useDatePickerShortcuts(
      shortcuts || defaultShortcuts,
    );

    const dateRangePickerErrorMessages = useDateRangePickerErrorMessages({
      error: dateRangePickerError,
      minDate,
      maxDate,
    });

    const handleError = (
      newError: DateRangeValidationError,
      updatedValue: DateRange<Dayjs>,
    ) => {
      setDateRangePickerError(newError);
      onError?.(newError, updatedValue);
    };

    return (
      <Box
        display="flex"
        flex="1"
        flexDirection="column"
        ref={ref}
        {...wrapperProps}
      >
        {labelWithTimezone && (
          <FormLabel
            component="p"
            error={
              !!errorMessage ||
              !!dateRangePickerError?.[0] ||
              !!dateRangePickerError?.[1]
            }
            required={required}
            sx={{ margin: 0 }}
          >
            {labelWithTimezone}
          </FormLabel>
        )}

        <FenrirDateRangePicker
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          name={name}
          onChange={onChange}
          onError={handleError}
          slots={{
            textField: DateRangeInputField,
          }}
          slotProps={{
            actionBar: {
              actions: ['clear', 'accept'],
            },
            field: {
              dateSeparator: t('ui:to'),
            },
            shortcuts: {
              items: filteredShortcuts,
            },
            textField: ({ position }) => ({
              className: hideMultiLabels ? 'MuiFormControl-hideLabel' : '',
              id: position === 'start' ? `${name}-start` : `${name}-end`,
              error:
                !!errorMessage ||
                !!dateRangePickerError?.[0] ||
                !!dateRangePickerError?.[1],
              helperText:
                errorMessage ||
                (position === 'start'
                  ? dateRangePickerErrorMessages.start || helperText?.start
                  : dateRangePickerErrorMessages.end || helperText?.end),
              placeholder:
                position === 'start' ? placeholder?.start : placeholder?.end,
              required,
              ...textFieldProps,
            }),
          }}
          timezone={timezone === 'local' ? 'system' : 'UTC'}
          value={value}
          {...pickerProps}
        />
      </Box>
    );
  },
);
